<template>
  <!-- The on-brand "face" is what the user sees. Google's real button is
       rendered underneath (vertically centred, clipped to the face width) and
       the face has pointer-events:none, so a click falls through to Google's
       button and opens the sign-in flow. The face is sized by its own padding,
       so the dark "Sign In" variant shrinks responsively and fits mobile. -->
  <div class="gbtn" :class="`gbtn--${variant}`">
    <div ref="real" class="gbtn__real"></div>
    <span class="gbtn__face">
      <span class="gbtn__label">{{ label }}</span>
      <span v-if="variant === 'light'" class="gbtn__icon"></span>
    </span>
  </div>
</template>

<script>
import { onMounted, ref } from 'vue'
import { ensureGoogleAuth, renderGoogleButton } from '@/plugin/googleAuth'

export default {
  name: 'GoogleButton',
  props: {
    label: { type: String, default: 'Sign Up with Google' },
    variant: { type: String, default: 'light' }, // 'light' (white CTA) | 'dark' (discreet)
    width: { type: Number, default: 280 }, // width of the hidden real Google button underneath
  },
  setup(props) {
    const real = ref(null)
    onMounted(async () => {
      await ensureGoogleAuth()
      renderGoogleButton(real.value, { width: props.width })
    })
    return { real }
  },
}
</script>

<style scoped lang="scss">
.gbtn {
  position: relative;
  display: inline-flex;
  overflow: hidden;
  border-radius: 18px;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.18);
}

.gbtn__real {
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
}

.gbtn__face {
  position: relative;
  z-index: 2;
  pointer-events: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  white-space: nowrap;
  transition: 0.3s background-color ease-in-out, 0.3s color ease-in-out;
}

.gbtn__label {
  font-weight: 500;
  transition: 0.3s color ease-in-out;
}

.gbtn__icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  background: url('../assets/images/gmail.svg') center / contain no-repeat;
}

/* Light — the white "Sign Up with Google" (used in the sign-in pop-up). */
.gbtn--light {
  .gbtn__face {
    background: var(--white-color, #ffffff);
    padding: 13px 28px;
  }
  .gbtn__label {
    color: #224efe;
    font-size: 17px;
  }
  &:hover .gbtn__face {
    background-color: #1d1f20;
  }
  &:hover .gbtn__label {
    color: var(--white-color, #ffffff);
  }
}

/* Dark — the discreet "Sign In" (header). */
.gbtn--dark {
  .gbtn__face {
    background: #1d1f20;
    padding: 20px 50px;
  }
  .gbtn__label {
    color: #7f8186;
    font-size: 2rem;
  }
  &:hover .gbtn__face {
    background-color: #224efe;
  }
  &:hover .gbtn__label {
    color: var(--white-color, #ffffff);
  }
}

@media screen and (max-width: 991px) {
  .gbtn--dark .gbtn__face {
    padding: 20px 30px;
  }
}

@media screen and (max-width: 560px) {
  .gbtn--dark .gbtn__face {
    padding: 15px 18px;
  }
  .gbtn--dark .gbtn__label {
    font-size: 1.8rem;
  }
}
</style>
